/**
 * API Client
 *
 * Axios instance configured for the Zoe API.
 * Handles base configuration, headers, and interceptors.
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { ENV } from '../constants/environment';

/**
 * Create and configure the axios instance
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: ENV.API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000, // 30 seconds
  });

  // Request interceptor - add API key and log requests in debug mode
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Add API key header if available
      const apiKey = process.env.REACT_APP_ZOE_API_KEY;
      if (apiKey) {
        config.headers['X-ZOE-API-KEY'] = apiKey;
      }

      if (ENV.DEBUG) {
        console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data);
      }

      return config;
    },
    (error: AxiosError) => {
      if (ENV.DEBUG) {
        console.error('[API Request Error]', error);
      }
      return Promise.reject(error);
    }
  );

  // Response interceptor - log responses in debug mode
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      if (ENV.DEBUG) {
        console.log(`[API Response] ${response.status}`, response.data);
      }
      return response;
    },
    (error: AxiosError) => {
      if (ENV.DEBUG) {
        console.error('[API Response Error]', error.response?.status, error.response?.data);
      }
      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * Singleton API client instance
 */
export const apiClient = createApiClient();

export default apiClient;
